import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../../lib/utils';

// TableCell defaults to `whitespace-nowrap` so single-line numeric and pill
// cells render compactly without the layout twitching when copy is borderline
// wrappable. Cells that contain wrapping text (multi-line titles, descriptions,
// long names) opt-in with `whitespace="normal"`. Default preserved so every
// existing call site behaves identically — this variant is purely additive.
//
// `numeric` is the canonical opt-in for cells rendering counts, money,
// percentages, or any quantity. Right-aligns the cell so digits stack by
// place value across rows, and applies `tabular-nums` so each digit takes
// the same width in proportional fonts — together the industry-standard
// "numbers line up" treatment (Stripe Dashboard, Linear, GitHub, Notion).
// Date / time columns (e.g. "Updated At") deliberately stay left-aligned;
// only quantities get this treatment.
const tableCellVariants = cva(
  'p-2 align-middle has-[[role=checkbox]]:pr-0 *:[[role=checkbox]]:translate-y-[2px]',
  {
    variants: {
      whitespace: {
        nowrap: 'whitespace-nowrap',
        normal: 'whitespace-normal',
      },
      numeric: {
        true: 'text-right tabular-nums',
        false: '',
      },
    },
    defaultVariants: {
      whitespace: 'nowrap',
      numeric: false,
    },
  },
);

export type TableCellProps = React.ComponentProps<'td'> &
  VariantProps<typeof tableCellVariants>;

// Table/TableHeader/TableBody/TableRow together implement a sticky-header
// table whose vertical scrollbar spans ONLY the body — not the header — using
// a single real <table> (no duplicate header markup, no scrollLeft syncing).
//
// The chain table-container → table → {thead, tbody} is one unconditional
// flex-column cascade: whatever height table-container ends up with (DataTable
// gives it one via either flex-1 (fillHeight mode) or a viewport-relative
// max-height (default mode) — see DataTable.tsx), `table` (flex-1, min-h-0)
// fills all of it, then splits that between `thead` (shrink-0 — its own
// natural height, never compressed) and `tbody` (flex-1, min-h-0 again) —
// so tbody always gets "whatever's left after the header," and its own
// overflow-y-auto is what actually scrolls once row content exceeds that.
// table-container's overflow-y is hidden — nothing should ever visibly
// scroll there now — while overflow-x-auto stays there (unchanged) so
// header and body, neither of which has an independent width, scroll
// horizontally together as one. tbody pairs its overflow-y-auto with an
// explicit overflow-x-hidden: leaving x unset would auto-upgrade it to
// `auto` too (CSS's "if one axis isn't visible, the other can't stay
// visible either" rule), giving tbody its own redundant, unsynced
// horizontal scrollbar.
//
// None of this needs `display:table`/`table-row-group` on table/thead/tbody
// — `table`/`flex-col` on <table> both keeps it out of the browser's
// anonymous-box table-fixup (which would otherwise wrap non-table-display
// children like a flexed thead/tbody in synthetic row/cell boxes) and
// drives this layout, and a flex item's internal table-type display value is
// blockified automatically. Each `TableRow` becomes its own `display:table`
// context instead (`table-fixed`) so table-layout:fixed's per-row column
// sizing — driven by the explicit per-cell pixel widths every cell already
// sets via colWidthStyle — still lines header and body columns up exactly,
// just resolved per-row rather than once for the whole table.
//
// CSS `display` overrides can affect implicit ARIA table semantics in some
// browser/AT combinations, so every part restates its role explicitly.
function Table({ className, ...props }: React.ComponentProps<'table'>) {
  return (
    <div
      data-slot="table-container"
      className="relative flex w-full flex-col overflow-x-auto overflow-y-hidden"
    >
      <table
        data-slot="table"
        role="table"
        className={cn('flex w-full min-h-0 flex-1 flex-col caption-bottom text-body-sm-regular', className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      role="rowgroup"
      className={cn('block shrink-0 [&_tr]:border-b', className)}
      {...props}
    />
  );
}

// A CSS-only limitation of the header/body split above: `position:sticky`
// resolves against the NEAREST ancestor that's a scroll container (any
// non-`visible` overflow, on EITHER axis) — not specifically the nearest
// ancestor that scrolls on the sticky element's own axis. tbody's overflow-y
// (needed for the vertical split) makes tbody itself that nearest scroll
// container for its own descendants, even though tbody never actually
// scrolls horizontally itself (table-container, one level up, is what
// genuinely does — and still correctly hosts thead's OWN sticky-left cell,
// which isn't nested inside anything with non-visible overflow). The result:
// a `sticky left-0` cell inside tbody stops tracking table-container's real
// horizontal scroll and just drifts with it instead.
//
// Fixes it directly: mark such cells `data-sticky-left` (see DataTable.tsx),
// and on every scroll of the real (table-container) scroller, translateX
// each one by the current scroll offset — reproducing exactly what
// `position:sticky` would do, driven by the ancestor that's actually
// authoritative. thead's own sticky-left cell is untouched (still native
// CSS, already correct) — this only compensates tbody's.
function useStickyLeftCompensation(tbodyRef: React.RefObject<HTMLTableSectionElement | null>) {
  React.useEffect(() => {
    const tbody = tbodyRef.current;
    const container = tbody?.closest<HTMLElement>('[data-slot="table-container"]');
    if (!tbody || !container) return;
    let rafId = 0;
    // Cells compensated on the last pass — diffed against the next pass so a
    // cell that stops being data-sticky-left (e.g. a runtime sticky toggle)
    // gets its transform explicitly cleared instead of left stale forever,
    // since nothing else in this component ever touches `transform`.
    let compensated = new Set<HTMLElement>();
    // Created only while a right-pinned cell exists: its offset depends on the
    // container width, which changes without a scroll. Other tables pay nothing.
    let resize: ResizeObserver | null = null;
    const trackResize = (needed: boolean) => {
      if (needed && !resize && typeof ResizeObserver !== 'undefined') {
        resize = new ResizeObserver(onScroll);
        resize.observe(container);
      } else if (!needed && resize) {
        resize.disconnect();
        resize = null;
      }
    };
    const sync = () => {
      rafId = 0;
      const x = container.scrollLeft;
      const transform = x ? `translateX(${x}px)` : '';
      const next = new Set<HTMLElement>();
      tbody.querySelectorAll<HTMLElement>('[data-sticky-left]').forEach((cell) => {
        cell.style.transform = transform;
        next.add(cell);
      });
      const rightCells = tbody.querySelectorAll<HTMLElement>('[data-sticky-right]');
      if (rightCells.length > 0) {
        // A right-pinned cell sits at the far end of the row; pull it back by
        // the scroll distance still remaining so it rests on the visible edge.
        const rightX = Math.min(0, x - (container.scrollWidth - container.clientWidth));
        const rightTransform = rightX ? `translateX(${rightX}px)` : '';
        rightCells.forEach((cell) => {
          cell.style.transform = rightTransform;
          next.add(cell);
        });
        container.toggleAttribute('data-right-overlap', rightX < 0);
      } else {
        container.removeAttribute('data-right-overlap');
      }
      trackResize(rightCells.length > 0);
      compensated.forEach((cell) => {
        if (!next.has(cell)) cell.style.transform = '';
      });
      compensated = next;
    };
    // Coalesce to one write per frame — trackpad/momentum scroll can fire
    // many native scroll events per frame otherwise.
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(sync);
    };
    sync();
    container.addEventListener('scroll', onScroll, { passive: true });
    // Re-sync whenever tbody's rows change (page/sort/filter swaps in new
    // cells) or a cell's sticky attribute itself changes —
    // otherwise a freshly-rendered or newly-(un)pinned cell can render
    // uncompensated/stale until the next real scroll event.
    const observer = new MutationObserver(sync);
    observer.observe(tbody, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-sticky-left', 'data-sticky-right'],
    });
    return () => {
      container.removeEventListener('scroll', onScroll);
      observer.disconnect();
      trackResize(false);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [tbodyRef]);
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  const ref = React.useRef<HTMLTableSectionElement>(null);
  useStickyLeftCompensation(ref);
  return (
    <tbody
      ref={ref}
      data-slot="table-body"
      role="rowgroup"
      className={cn('block min-h-0 flex-1 overflow-y-auto overflow-x-hidden [&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      role="row"
      // `table table-fixed w-full`: with `table`/`thead`/`tbody` all forced to
      // `display:block` (see Table above), each row becomes its OWN
      // `display:table` context — table-layout:fixed then sizes this row's
      // cells from their own explicit pixel widths (colWidthStyle), the same
      // as every other row, so columns still line up exactly across rows.
      // `border-separate border-spacing-0` (was on the table as a whole)
      // moves here for the same reason — it's what keeps a sticky cell's
      // background rendering correctly during horizontal scroll.
      className={cn(
        'table w-full table-fixed border-separate border-spacing-0 border-b transition-colors hover:bg-muted/50 has-aria-expanded:bg-muted/50 data-[state=selected]:bg-muted',
        className,
      )}
      {...props}
    />
  );
}

// TableHead mirrors TableCell's `whitespace` variant for the same reason:
// column headers are usually short and default to `nowrap`, but the rare
// long header (multi-word, kebab-style) can opt into wrap.
const tableHeadVariants = cva(
  'h-10 px-2 text-left align-middle font-medium text-foreground has-[[role=checkbox]]:pr-0 *:[[role=checkbox]]:translate-y-[2px]',
  {
    variants: {
      whitespace: {
        nowrap: 'whitespace-nowrap',
        normal: 'whitespace-normal',
      },
    },
    defaultVariants: {
      whitespace: 'nowrap',
    },
  },
);

export type TableHeadProps = React.ComponentProps<'th'> &
  VariantProps<typeof tableHeadVariants>;

function TableHead({ className, whitespace, ...props }: TableHeadProps) {
  return (
    <th
      data-slot="table-head"
      role="columnheader"
      className={cn(tableHeadVariants({ whitespace }), className)}
      {...props}
    />
  );
}

function TableCell({ className, whitespace, numeric, ...props }: TableCellProps) {
  return (
    <td
      data-slot="table-cell"
      role="cell"
      className={cn(tableCellVariants({ whitespace, numeric }), className)}
      {...props}
    />
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-4 text-body-sm-regular text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
